import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { toInsights, type Insights } from './insightsWire'

/** The endpoint's maximum, so `insight_count` is the true count; only the first renders. */
export const INSIGHTS_LIMIT = 20
export const INSIGHTS_ERROR = "Insights couldn't be loaded."

export class InsightsRequestError extends Error {
  readonly status: number
  constructor(status: number) {
    super(`Insights request failed with ${status}`)
    this.name = 'InsightsRequestError'
    this.status = status
  }
}

export type InsightsQuery = {
  tz: string
  /** `useEntitlement('spending_insights')`: free fetches nothing. */
  enabled: boolean
}

/**
 * A LIVE surface: computed on request, so `generated_at` is the request and not
 * a bank sync. No `asOf`, so the state is never stale. A body that breaks the
 * contract is a query error, never partial data.
 */
export function useInsights({ tz, enabled }: InsightsQuery): AsyncState<Insights> {
  const query = useQuery({
    queryKey: keys.insights.list({ tz, limit: INSIGHTS_LIMIT }),
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/v1/insights', {
        params: { query: { tz, limit: INSIGHTS_LIMIT } },
      })
      if (error !== undefined || data === undefined) throw new InsightsRequestError(response.status)
      return toInsights(data)
    },
    enabled,
  })
  return fromQuery(query, {
    message: () => INSIGHTS_ERROR,
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (d) => d.insights.length === 0,
  })
}
